/* eslint-disable */

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Spin, Modal, message } from 'antd';
import Main from '../../components/layout/Main';
import DOMPurify from 'dompurify';
import './singleHoroscope.scss';
import { getNewSingleHoroscopeRequest } from '../../core/horoscope/horoscopeActions';
import HoroscopeCarousel from '../../components/HoroscopeCarousel';
import useLanguage from '../../core/language/useLanguage';
import { getSelectedTranslation, getTranslation } from '../../core/utils/helpers';
import OneOffPurchaseButton from '../../components/OneOffPurchaseButton';
import { selectSelectedHoroscope } from '../../core/horoscope/horoscopeActions';
import YellowThemeButton from '../../components/YellowThemeButton';
import SEO from '../../components/SEO';
import { ZODIAC_SIGNS, getHoroscopeSEODescription, getHoroscopeJsonLd } from '../../components/SEO/seoConfig';
import app from '../../core/constants';
import AiChat from '../../components/AiChat';
import FormattedText from '../../components/AiChat/FormattedText';
import useAi from '../../core/ai/useAi';
import { QUESTION_LIMIT } from '../../core/ai/aiReducer';
import { BlingIcon } from '../../components/icons';
import { CopyOutlined } from '@ant-design/icons';
import Accordion from '../../components/Accordion';

const SingleHoroscope = (props) => {
    const [elRefs, setElRefs] = useState([]);
    const [showAppStoreLink, setShowAppStoreLink] = useState(false);

    const horoscope = useSelector(state => state.horoscope);
    const { isFetching, singleHoroscope } = horoscope;
    const { userData } = useSelector(state => state.user);

    const [activeHoroscopeIndex, setActiveHoroscopeIndex] = useState(0);
    const [hasComputedIndex, setHasComputedIndex] = useState(false);

    const { language } = useLanguage();
    const { timePeriod, horoscopeSign } = props.match.params;
    const user = useSelector(state => state.user);
    const { isLoggedIn } = user;

    const dispatch = useDispatch();

    // AI Integration
    const { ai, aiSummaryRequest, aiQuestionRequest, aiClearChat, rateLimitInfo } = useAi();
    const [questionsAsked, setQuestionsAsked] = useState(0);
    const [showAiChat, setShowAiChat] = useState(false);
    const lastSummaryPeriodRef = useRef(null); // Track which period index the current summary is for

    const isAndroidDevice = () => {
        return /Android/i.test(navigator.userAgent);
    };

    useEffect(() => {
        const getHoroscopeData = async () => {
            try {
                const userAuthenticated = isLoggedIn;
                dispatch(getNewSingleHoroscopeRequest({ timePeriod, horoscopeSign, userAuthenticated }));
            } catch (error) {
                console.log(error.message);
            }
        };
        getHoroscopeData();
        window.scrollTo(0, 0);
        if (isAndroidDevice()) {
            setShowAppStoreLink(true);
        }
    }, []);

    useEffect(() => {
        if (singleHoroscope?.horoscopeData?.length > 0) {
            const now = moment();
            let currentIndex = 0;

            // Find the horoscope that matches the current week/month
            const foundIndex = singleHoroscope.horoscopeData.findIndex((item) => {
                const date = moment(item.date);
                if (!date.isValid()) return false;

                // Weekly horoscope (type '1')
                if (item.type === '1') {
                    const weekEnd = moment(date).add(6, 'days');
                    return now.isBetween(date, weekEnd, 'day', '[]');
                }

                // Monthly horoscope (type '2')
                if (item.type === '2') {
                    return now.isSame(date, 'month') && now.isSame(date, 'year');
                }

                // Yearly horoscope (type '3')
                if (item.type === '3') {
                    return now.isSame(date, 'year');
                }

                return false;
            });

            // Use found index if valid, otherwise default to the last item
            currentIndex = foundIndex >= 0 ? foundIndex : singleHoroscope.horoscopeData.length - 1;
            setActiveHoroscopeIndex(currentIndex);
            setHasComputedIndex(true);
        }
    }, [singleHoroscope]);

    useEffect(() => {
        if (singleHoroscope?.horoscopeData && activeHoroscopeIndex < singleHoroscope.horoscopeData.length) {
            dispatch(selectSelectedHoroscope(singleHoroscope?.horoscopeData[activeHoroscopeIndex]));
        }
    }, [activeHoroscopeIndex]);

    useEffect(() => {
        setElRefs((elRefs) =>
            Array(singleHoroscope?.horoscopeData?.length)
                .fill()
                .map((_, i) => elRefs[i] || React.createRef())
        );
    }, [singleHoroscope]);

    // Clear AI chat when changing horoscope sign or time period (not when changing week/month view)
    useEffect(() => {
        aiClearChat();
        setQuestionsAsked(0);
        setShowAiChat(false);
        lastSummaryPeriodRef.current = null; // Reset so new summary is fetched for new sign/period
    }, [horoscopeSign, timePeriod]);

    const handleOpenInApp = () => {
        const intentUrl = `intent://HoroscopeDetail?timePeriod=${timePeriod}&horoscopeSign=${horoscopeSign}#Intent;scheme=prakashastrology;package=com.prakash.astrology;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.prakash.astrology;end`;
        window.location.href = intentUrl;
    };

    const scrollToPart = (index) => {
        if (elRefs[index] && elRefs[index].current) {
            elRefs[index].current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // if single horoscope is an empty object, return null
    if (Object.keys(singleHoroscope).length === 0) {
        return null;
    }

    if (!singleHoroscope?.horoscopeData || singleHoroscope?.horoscopeData?.length === 0) {
        return null;
    }

    const activeHoroscope = singleHoroscope?.horoscopeData[activeHoroscopeIndex];
    const horoscopeTranslation = getSelectedTranslation(activeHoroscope?.translations, language?.selectedLanguage?.id);
    
    const isUserSubscribed = userData?.active_subscriptions?.some(
        subscription => subscription?.status === "active" && subscription?.horoscope?.key?.toLowerCase() === horoscopeSign.toLowerCase()
    ) || false;

    const hasTrialAccess = userData?.hasFreeTrial && userData?.freeTrialDaysLeft > 0;
    const hasAccess = app.ENABLE_FREE_PREMIUM || isUserSubscribed || hasTrialAccess;

    // const renderTrialTimer = () => {
    //     if (hasTrialAccess && !isUserSubscribed) {
    //         return <CountDownTimer timerLeft={userData?.freeTrialDaysLeft} />;
    //     }
    //     return null;
    // };

    const showBuyButton = !hasAccess;
    
    const videoCode = hasAccess ? horoscopeTranslation?.video_link : horoscopeTranslation?.video_link_preview;

    if (!horoscopeTranslation && !activeHoroscope) {
        return null;
    }

    const renderHoroscopePart = (partData, partDataIndex) => {
        const horoscopePartLanguage = getSelectedTranslation(partData?.translations, language?.selectedLanguage?.id);
        return (
            <div
                className="chapter-block"
                key={`single-part-${partData.id}`}
                ref={elRefs[partDataIndex]}>
                <h3>{horoscopePartLanguage?.name}</h3>
                {horoscopePartLanguage?.video_link
                    && (
                        <div className="video-block">
                            <iframe src={`https://www.youtube.com/embed/${horoscopePartLanguage?.video_link}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
                        </div>
                    )}
                <div>{horoscopePartLanguage?.description}</div>
            </div>
        );
    };

    const getCarouselTitle = (horoscopeData) => {
        const date = horoscopeData?.date;
        if (!date || !moment(date).isValid()) {
            if (horoscopeData?.type === '1') {
                return 'Weekly';
            }
            if (horoscopeData?.type === '2') {
                return 'Monthly';
            }
            if (horoscopeData?.type === '3') {
                return 'Yearly';
            }
            return 'Date not found';
        }

        if (horoscopeData?.type === '1') {
            return `${moment(date).format('MMMM DD')} - ${moment(date).add(6, 'day').format('MMMM DD')}`;
        }
        if (horoscopeData?.type === '2') {
            return `${moment(date).format('MMMM YYYY')}`;
        }
        if (horoscopeData?.type === '3') {
            return moment(date).format('YYYY');
        }
        return 'Date not found';
    };

    const carouselData = singleHoroscope?.horoscopeData?.map((item, index) => ({
        id: index + 1,
        onClick: () => setActiveHoroscopeIndex(index),
        title: getCarouselTitle(item),
    }));

    const getHoroscopeType = () => {
        if (activeHoroscope?.type === '1') {
            return 'Weekly Horoscope';
        }
        if (activeHoroscope?.type === '2') {
            return 'Monthly Horoscope';
        }
        return 'Annual Horoscope';
    };

    const cleanHTML = DOMPurify.sanitize(horoscopeTranslation?.description_preview);

    // Helper function to validate YouTube video code
    const isValidYouTubeVideo = (code) => {
        if (!code) return false;
        // Reject if it looks like a numeric ID (not a YouTube video ID)
        if (code.length === 9 && /^[0-9]+$/.test(code)) {
            return false;
        }
        // Reject if it contains slashes (likely a URL path, not a video ID)
        if (code.indexOf('/') > -1) {
            return false;
        }
        return true;
    };

    // Render Special Forecasts section
    const renderSpecialForecasts = () => {
        // Get forecasts from activeHoroscope (handles both camelCase and snake_case)
        const forecasts = activeHoroscope?.specialForecasts || activeHoroscope?.special_forecasts || [];

        if (!forecasts || forecasts.length === 0) {
            return null;
        }

        return (
            <div className="special-forecasts-container">
                <h2 className="special-forecasts-title">
                    {getTranslation(language, 'special_forecasts', 'Special Forecasts')}
                </h2>
                {forecasts.map((forecast) => {
                    // Get translation for current language
                    const forecastTranslation = getSelectedTranslation(
                        forecast?.translations,
                        language?.selectedLanguage?.id
                    );

                    // Check if valid video exists
                    const forecastVideoCode = forecastTranslation?.video_link;
                    const hasVideo = forecastVideoCode && isValidYouTubeVideo(forecastVideoCode);

                    return (
                        <Accordion
                            key={forecast.id}
                            title={forecastTranslation?.name || forecast.name || 'Forecast'}
                        >
                            {/* Video (if exists) */}
                            {hasVideo && (
                                <div className="accordion__video-block">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${forecastVideoCode}`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        referrerPolicy="strict-origin-when-cross-origin"
                                    />
                                </div>
                            )}

                            {/* Description text */}
                            {forecastTranslation?.description && (
                                <div
                                    className="special-forecast-description"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(forecastTranslation.description)
                                    }}
                                />
                            )}
                        </Accordion>
                    );
                })}
            </div>
        );
    };

    // AI Helper Functions - Get ALL horoscopes data for cross-week/month queries
    const getAllHoroscopesText = () => {
        const horoscopeDataArray = singleHoroscope?.horoscopeData || [];
        let text = '';

        horoscopeDataArray.forEach((horoscopeItem, index) => {
            const itemTranslation = getSelectedTranslation(horoscopeItem?.translations, language?.selectedLanguage?.id);
            const periodTitle = getCarouselTitle(horoscopeItem);

            // Add period header
            text += `\n\n=== ${periodTitle} ===\n`;
            text += `${itemTranslation?.name || ''}\n`;

            if (itemTranslation?.description_preview) {
                text += `${itemTranslation.description_preview}\n`;
            }

            if (horoscopeItem?.parts?.length > 0) {
                horoscopeItem.parts.forEach(part => {
                    const partT = getSelectedTranslation(part.translations, language?.selectedLanguage?.id);
                    text += `\n${partT?.name || ''}\n${partT?.description || ''}`;
                });
            } else if (itemTranslation?.description) {
                text += `\n${itemTranslation.description}`;
            }
        });

        return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    };

    // Get current horoscope text (for summary - focuses on current period)
    const getCurrentHoroscopeText = () => {
        // Get the current active horoscope directly from the array to ensure we have fresh data
        const currentHoroscope = singleHoroscope?.horoscopeData?.[activeHoroscopeIndex];
        const currentTranslation = getSelectedTranslation(currentHoroscope?.translations, language?.selectedLanguage?.id);

        // Include the period date range so AI knows the correct period
        const periodLabel = getCarouselTitle(currentHoroscope);

        // Build text with explicit period instruction for AI
        let text = `IMPORTANT: This horoscope is ONLY for the period: ${periodLabel}\n\n`;
        text += `${currentTranslation?.name || ''}\n`;
        text += `Period: ${periodLabel}\n\n`;

        if (currentHoroscope?.parts?.length > 0) {
            currentHoroscope.parts.forEach(part => {
                const partT = getSelectedTranslation(part.translations, language?.selectedLanguage?.id);
                text += `\n${partT?.name || ''}\n${partT?.description || ''}`;
            });
        } else if (currentTranslation?.description) {
            text += `\n${currentTranslation.description}`;
        } else if (currentTranslation?.description_preview) {
            text += `\n${currentTranslation.description_preview}`;
        }

        // Debug log to verify correct content
        console.log('[AI Summary] Period:', periodLabel, 'Index:', activeHoroscopeIndex);

        return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    };

    const handleGetSummary = () => {
        if (activeHoroscope) {
            // Clear previous chat and request new summary for current period
            aiClearChat();
            aiSummaryRequest({ content: getCurrentHoroscopeText() });
            lastSummaryPeriodRef.current = activeHoroscopeIndex;
        }
    };

    const openAiChat = () => {
        // Always request new summary if period changed or no summary exists
        const needsNewSummary = !ai.get('summary') || lastSummaryPeriodRef.current !== activeHoroscopeIndex;
        if (needsNewSummary && !ai.get('isLoadingSummary')) {
            handleGetSummary();
        }
        setShowAiChat(true);
    };

    const closeAiChat = () => {
        setShowAiChat(false);
    };

    const handleAskQuestion = (question) => {
        // Q&A uses ALL horoscopes data for cross-week/month queries
        aiQuestionRequest({ question, content: getAllHoroscopesText() });
        setQuestionsAsked(prev => prev + 1);
    };

    const handleCopySummary = () => {
        const summary = ai.get('summary');
        if (summary) {
            window.navigator.clipboard.writeText(summary).then(() => {
                message.success('Summary copied to clipboard');
            }).catch(() => {
                message.error('Failed to copy');
            });
        }
    };

    // Question limit applies to everyone - upsell to consultations
    const questionLimit = QUESTION_LIMIT;

    // Rate limit message
    const getRateLimitMessage = () => {
        if (rateLimitInfo.isDailyLimitReached) {
            return 'Daily limit reached (50 requests/day). Please try again tomorrow.';
        }
        if (rateLimitInfo.isHourlyLimitReached) {
            return 'Hourly limit reached (10 requests/hour). Please try again later.';
        }
        return '';
    };

    // SEO - Get sign info for meta tags
    const signInfo = ZODIAC_SIGNS.find(s => s.key.toLowerCase() === horoscopeSign?.toLowerCase());
    const signName = signInfo?.name || horoscopeSign;
    const pageUrl = `/horoscope/${timePeriod}/${horoscopeSign}`;

    return (
        <>
        <SEO
            title={`${signName} ${timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1).replace(/-/g, ' ')} Horoscope`}
            description={getHoroscopeSEODescription(signName, timePeriod)}
            url={pageUrl}
            type="article"
            jsonLd={getHoroscopeJsonLd(signName, timePeriod, pageUrl)}
        />
        <Main
            className="align-center"
            headerTitle={(
                <div>
                    <img style={{ height: 70, width: 70 }}
                        src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                        alt="logo" />
                </div>
            )}
            menuWidth={350}
            menuPlacement="right"
            displayRightMenu
            floatingHeader
            showFooter={false}
            scrollTo={scrollToPart}
            backLink={(
                <div style={{ display: 'flex' }}>
                    <Link to="/dashboard" className="backlink" style={{ marginBottom: '24px' }} />
                </div>
            )}>
            {isFetching ? (
                <div className="loaderSpacer">
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {showAppStoreLink && (
                        <Modal
                            visible={showAppStoreLink}
                            onCancel={() => setShowAppStoreLink(false)}
                            footer={null}
                            className="open-in-app-modal"
                        >
                            <p className="open-in-app-modal-title">View this horoscope in the app?</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                <YellowThemeButton
                                    onClick={() => setShowAppStoreLink(false)}
                                >
                                    Continue in Browser
                                </YellowThemeButton>
                                <YellowThemeButton
                                    onClick={handleOpenInApp}
                                >
                                    Open in App
                                </YellowThemeButton>
                            </div>
                        </Modal>
                    )}
                    <div className="single-horoscope-content">
                        <div className="horoscope-header">
                            {activeHoroscope?.type !== '3' && hasComputedIndex && <div className="horoscope-carousel-wrapper"><HoroscopeCarousel data={carouselData} activeSlide={activeHoroscopeIndex + 1} /></div>}
                            <h1 className="page-title">{horoscopeTranslation?.name?.split(' ')[0] || signName}</h1>
                            <h3 className="page-subtitle">{getHoroscopeType()}</h3>
                            {/* {renderTrialTimer()} */}
                            {showBuyButton && (
                                <div className="one-off-buy-btn-wrapper">
                                    <OneOffPurchaseButton
                                        btnClass=""
                                        title={getCarouselTitle(activeHoroscope)}
                                        horoscopeDataId={activeHoroscope?.id}
                                        horoscopeType={activeHoroscope?.type} />
                                </div>
                            )}
                        </div>
                        <div className="single-horoscope-content-wrapper">
                            {!isEmpty(videoCode) ? (
                                <div className="video-block">
                                    <iframe src={`https://www.youtube.com/embed/${videoCode}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
                                </div>
                            ) : null }
                            {hasAccess ? (
                                activeHoroscope?.parts.length > 0 ? (
                                    <div className="horoscope-parts-wrapper">
                                        {activeHoroscope?.parts
                                            .map((part, index) => renderHoroscopePart(part, index))}
                                    </div>
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: cleanHTML }} />
                                )
                            ) : (
                                horoscopeTranslation?.description_preview ? (
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(horoscopeTranslation.description_preview) }} />
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: cleanHTML }} />
                                )
                            )}
                            {/* Special Forecasts Section - only show if user has access */}
                            {hasAccess && renderSpecialForecasts()}
                        </div>
                    </div>
                </>


            )}
        </Main>

        {/* AI Floating Button */}
        <button
            type="button"
            className="floating-ai-button"
            onClick={openAiChat}
        >
            <div className="floating-ai-button-text">
                Summarize <BlingIcon />
            </div>
        </button>

        {/* AI Chat Panel */}
        {showAiChat && (
            <div className="floating-ai-chat">
                <div className="floating-ai-chat-header">
                    <span className="floating-ai-chat-title">AI Summary</span>
                    <button
                        type="button"
                        className="floating-ai-chat-close"
                        onClick={closeAiChat}
                    >
                        &times;
                    </button>
                </div>

                {ai.get('isLoadingSummary') && (
                    <div className="ai-loading">
                        <Spin size="small" />
                        <span>AI is thinking...</span>
                    </div>
                )}

                {ai.get('error') && !ai.get('isLoadingSummary') && (
                    <div className="ai-error">
                        <p>{ai.get('error')}</p>
                        <button
                            type="button"
                            className="retry-btn"
                            onClick={handleGetSummary}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {ai.get('summary') && (
                    <div className="ai-summary-wrapper">
                        <div className="ai-summary">
                            <FormattedText text={ai.get('summary')} />
                        </div>
                        <button
                            type="button"
                            className="copy-summary-btn"
                            onClick={handleCopySummary}
                            title="Copy summary"
                        >
                            <CopyOutlined />
                        </button>
                    </div>
                )}

                {ai.get('summary') && (
                    <AiChat
                        chatHistory={ai.get('chatHistory')}
                        onSend={handleAskQuestion}
                        isLoading={ai.get('isLoadingAnswer')}
                        questionLimit={questionLimit}
                        questionsAsked={questionsAsked}
                        isRateLimited={rateLimitInfo.isLimitReached}
                        rateLimitMessage={getRateLimitMessage()}
                    />
                )}
            </div>
        )}
        </>
    );
};

export default SingleHoroscope;

SingleHoroscope.propTypes = {
    match: PropTypes.object.isRequired,
};
